import type { inferRouterInputs } from '@trpc/server';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';
import type { AppRouter } from '../../../../src/admin/routers';
import { unifiedDiff } from './diff';
import { KEYS_WITH_SF } from './messageKeyMeta';
import { renderPreview } from './preview';

const PLACEHOLDERS: readonly { token: string; sf: boolean }[] = [
  { token: '{LINE:Nickname}', sf: false },
  { token: '{SF:Name}', sf: true },
  { token: '{SF:CustomerId__C}', sf: true },
];

// Mirrors the server's templatesRouter input enum so the URL param can flow
// straight into the typed tRPC procedures.
type TemplateKey = inferRouterInputs<AppRouter>['templates']['get']['key'];

export function TemplateEdit(): JSX.Element {
  const params = useParams();
  const key = (params.key ?? '') as TemplateKey;
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.templates.get.useQuery({ key });
  const [draft, setDraft] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const update = trpc.templates.update.useMutation({
    onSuccess: async () => {
      await utils.templates.list.invalidate();
      await utils.templates.get.invalidate({ key });
      setConfirming(false);
    },
  });

  const canEdit = user?.role === 'editor' || user?.role === 'admin';
  const canUseSf = KEYS_WITH_SF.has(key);
  const text = draft ?? data?.text ?? '';
  const preview = renderPreview(text);
  const saveDisabled =
    !canEdit || text.length === 0 || preview.errors.length > 0 || text === data?.text;

  if (isLoading) return <p>読み込み中...</p>;

  return (
    <div className="max-w-3xl">
      <header className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">{key}</h1>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={() => navigate(`/templates/${key}/history`)}
          >
            履歴
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                disabled={draft === null}
                onClick={() => setDraft(null)}
              >
                破棄
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:bg-gray-300"
                disabled={saveDisabled}
                onClick={() => setConfirming(true)}
              >
                保存
              </button>
            </>
          )}
        </div>
      </header>
      <div className="grid grid-cols-[1fr,200px] gap-4">
        <textarea
          className="min-h-[200px] rounded border p-2 font-mono text-sm"
          readOnly={!canEdit}
          value={text}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="space-y-1">
          <div className="text-xs font-semibold text-gray-600">プレースホルダ</div>
          {PLACEHOLDERS.map((p) => {
            const disabled = !canEdit || (p.sf && !canUseSf);
            return (
              <button
                key={p.token}
                type="button"
                disabled={disabled}
                title={p.sf && !canUseSf ? 'SF プレースホルダはこのキーでは使えません' : undefined}
                className="block w-full rounded border px-2 py-1 text-left text-xs hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400"
                onClick={() => setDraft((cur) => (cur ?? data?.text ?? '') + p.token)}
              >
                {p.token}
              </button>
            );
          })}
        </div>
      </div>
      <section className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
        <div className="mb-2 text-xs font-semibold text-gray-600">プレビュー</div>
        {preview.errors.length > 0 ? (
          <div className="rounded bg-red-100 p-2 text-sm text-red-700">
            未対応のプレースホルダ: {preview.errors.join(', ')}
          </div>
        ) : (
          <pre className="whitespace-pre-wrap text-sm">{preview.text}</pre>
        )}
        {key === 'greeting' && (
          <div className="mt-2 text-xs text-gray-500">+ FAQ 画像が自動添付されます（編集不可）</div>
        )}
        <div className="mt-2 text-[10px] text-gray-400">
          サンプル値: Nickname=太郎 / Name=山田太郎 / CustomerId=W12345
        </div>
      </section>

      {confirming && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="w-[600px] rounded bg-white p-4 shadow">
            <h2 className="mb-2 text-base font-semibold">変更を確認して保存</h2>
            <div className="mb-3 max-h-64 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
              {unifiedDiff(data?.text ?? '', text).map((line, idx) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
                  key={idx}
                  className={
                    line.kind === 'add'
                      ? 'bg-green-100'
                      : line.kind === 'remove'
                        ? 'bg-red-100'
                        : ''
                  }
                >
                  {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
                  {line.text}
                </div>
              ))}
            </div>
            <div className="mb-3 rounded bg-gray-50 p-2 text-sm">
              <div className="mb-1 text-xs font-semibold text-gray-600">プレビュー (after)</div>
              <pre className="whitespace-pre-wrap">{preview.text}</pre>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setConfirming(false)}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
                onClick={() => update.mutate({ key, text })}
                disabled={update.isPending}
              >
                保存して反映
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
